import { CalendarPlus } from "lucide-react";
import { addDays, parseISO, format } from "date-fns";
import { useLang } from "@/lib/i18n";
import { usePositions } from "@/lib/positions";
import { VAGUE_TIMES, fmtTime } from "@/lib/format";

const TZ = "America/Toronto";
const stamp = (date, time) => `${date.replace(/-/g, "")}T${time.replace(":", "")}00`;
const esc = (s) => String(s || "").replace(/\\/g, "\\\\").replace(/([,;])/g, "\\$1").replace(/\n/g, "\\n");
const nextDay = (date) => format(addDays(parseISO(date), 1), "yyyy-MM-dd");

const shiftTimes = (a) => {
  const ev = a.event;
  const start = a.slot.start_time || ev.start_time;
  const end = a.slot.end_time || ev.end_time || start;
  if (VAGUE_TIMES.includes(start) || VAGUE_TIMES.includes(end)) {
    return [ev.date.replace(/-/g, ""), nextDay(ev.date).replace(/-/g, ""), true];
  }
  const endDate = end <= start ? nextDay(ev.date) : ev.date;
  return [stamp(ev.date, start), stamp(endDate, end), false];
};

export const AddToCalendar = ({ a }) => {
  const { t } = useLang();
  const { label } = usePositions();
  const ev = a.event;
  const [start, end, allDay] = shiftTimes(a);
  const title = `${ev.name} — ${label(a.slot.position_key)}`;
  const where = ev.address && ev.location && ev.address.startsWith(ev.location) ? ev.address : [ev.location, ev.address].filter(Boolean).join(", ");
  const times = `${fmtTime(a.slot.start_time || ev.start_time, t)}–${fmtTime(a.slot.end_time || ev.end_time, t)}`;
  const details = [allDay && times, ev.report_time && `${t("c.reporting")} ${fmtTime(ev.report_time, t)}`, a.slot.instructions, "Atlas Event Services"].filter(Boolean).join("\n");
  const google = `https://calendar.google.com/calendar/render?${new URLSearchParams({ action: "TEMPLATE", text: title, dates: `${start}/${end}`, ...(allDay ? {} : { ctz: TZ }), location: where, details })}`;
  const dt = allDay ? [`DTSTART;VALUE=DATE:${start}`, `DTEND;VALUE=DATE:${end}`] : [`DTSTART;TZID=${TZ}:${start}`, `DTEND;TZID=${TZ}:${end}`];
  const ics = ["BEGIN:VCALENDAR", "VERSION:2.0", "PRODID:-//Atlas Event Services//Horaire//FR", "CALSCALE:GREGORIAN", "METHOD:PUBLISH", "BEGIN:VEVENT",
    `UID:${a.id}@atlas-event-services`, `DTSTAMP:${new Date().toISOString().replace(/[-:]/g, "").split(".")[0]}Z`,
    ...dt, `SUMMARY:${esc(title)}`, `LOCATION:${esc(where)}`, `DESCRIPTION:${esc(details)}`,
    "END:VEVENT", "END:VCALENDAR"].join("\r\n");
  const btn = "press flex-1 inline-flex items-center justify-center gap-1.5 h-10 rounded-full border border-black/15 text-xs hover:border-[#C5A059] hover:text-[#D9B872]";
  return (
    <div className="mt-4" data-testid={`add-to-calendar-${a.id}`}>
      <div className="eyebrow mb-2 flex items-center gap-1.5"><CalendarPlus className="w-3.5 h-3.5 text-[#C5A059]" />{t("cal.add")}</div>
      <div className="flex gap-2">
        <a href={google} target="_blank" rel="noreferrer" className={btn} data-testid={`add-google-${a.id}`}>Google Agenda</a>
        <a href={`data:text/calendar;charset=utf-8,${encodeURIComponent(ics)}`} download={`atlas-${ev.date}.ics`} className={btn} data-testid={`add-ics-${a.id}`}>Apple / Outlook</a>
      </div>
    </div>
  );
};
